import {
  IAiOnboardingFinalProfileFields,
  IAiOnboardingProfileFields,
  IAiOnboardingSnapshot,
} from '../models/AiOnboardingModels';

import {
  SaveOnboardingDetailsFields,
} from './types';
import { TypeOfUser } from '../helpers/Constants';

const requiredAiOnboardingProfileFieldNames:
  Array<keyof IAiOnboardingProfileFields> = [
    'role',
    'preferdLanguage',
    'firstName',
    'lastName',
    'professionalCapacityId',
    'englishLevel',
    'frenchLevel',
    'otherLanguages',
    'incorporated',
    'isMethadonePrepKnowledge',
    'isComfortDispillVerification',
    'selectedSoftwares',
    'selectedContactPreferences',
    'address',
    'city',
    'province',
    'postalCode',
    'phone',
    'mobile',
    'preferredWorkingDays',
    'preferredReplacementDuration',
    'remoteRegion',
  ];

const requiredNonEmptyStringFieldNames:
  Array<keyof IAiOnboardingProfileFields> = [
    'role',
    'preferdLanguage',
    'firstName',
    'lastName',
    'professionalCapacityId',
    'address',
    'city',
    'province',
    'postalCode',
    'mobile',
    'preferredReplacementDuration',
  ];

// #technician-changes: answers only the technician agent collects.
const requiredTechnicianProfileFieldNames:
  Array<keyof IAiOnboardingProfileFields> = [
    'isNonSterilCompoundPrepKnowledge',
    'handlingHospitalDischargeRating',
    'isClickSanteKnowledge',
    'isEmpegoKnowledge',
  ];

const isNonEmptyString = (value: unknown) =>
  typeof value === 'string' && Boolean(value.trim());

const isPresent = (value: unknown) =>
  value !== null && value !== undefined;

const isNonNegativeNumber = (value: unknown): value is number =>
  typeof value === 'number'
  && Number.isFinite(value)
  && value >= 0;

/**
 * The AI onboarding script does not ask about availability reminders, so the
 * value is never user-provided and is always stored as true.
 */
const aiOnboardingAvailabilityReminder = true;

const phoneNumberGroupLength = 3;
const northAmericanPhoneNumberLength = 10;
const northAmericanCountryCode = '1';

export const normalizeAiOnboardingPhoneNumber = (phoneNumber: string) => {
  const trimmedPhoneNumber = phoneNumber.trim();
  const digits = trimmedPhoneNumber.replace(/\D/g, '');
  const localNumber = digits.length === northAmericanPhoneNumberLength + 1
    && digits.startsWith(northAmericanCountryCode)
    ? digits.slice(1)
    : digits;

  if (localNumber.length !== northAmericanPhoneNumberLength) {
    return trimmedPhoneNumber;
  }

  const firstGroupEnd = phoneNumberGroupLength;
  const secondGroupEnd = phoneNumberGroupLength * 2;

  return [
    localNumber.slice(0, firstGroupEnd),
    localNumber.slice(firstGroupEnd, secondGroupEnd),
    localNumber.slice(secondGroupEnd),
  ].join('-');
};

const canadianPostalCodeLength = 6;
const canadianPostalCodeGroupLength = 3;

/**
 * Speech recognition returns postal codes in whatever shape it heard them, so
 * the saved value only matches the format the signup forms expect once the
 * separators are dropped and the letters are capitalized. A value of another
 * length is left alone for validation to report.
 */
export const normalizeAiOnboardingPostalCode = (postalCode: string) => {
  const trimmedPostalCode = postalCode.trim();
  const compactPostalCode = trimmedPostalCode
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase();

  if (compactPostalCode.length !== canadianPostalCodeLength) {
    return trimmedPostalCode;
  }

  return [
    compactPostalCode.slice(0, canadianPostalCodeGroupLength),
    compactPostalCode.slice(canadianPostalCodeGroupLength),
  ].join(' ');
};

// #technician-changes: a technician's experience counts from the diploma year,
// while graduationYear holds the year they started relief work.
const getExperienceStartYear = (profile: SaveOnboardingDetailsFields) => {
  if (profile.role === TypeOfUser.technician) {
    return profile.techGraduationYear ?? null;
  }

  return profile.graduationYear ?? null;
};

const isTechnicianWithStatedExperience = (
  profile: SaveOnboardingDetailsFields,
): profile is SaveOnboardingDetailsFields & { yearsOfExperience: number } =>
  profile.role === TypeOfUser.technician
  && isNonNegativeNumber(profile.yearsOfExperience);

const getProfileWithExperienceFromStartYear = (
  profile: SaveOnboardingDetailsFields,
  experienceStartYear: number,
  currentYear: number,
): SaveOnboardingDetailsFields => {
  // #technician-changes: only a technician can state years of experience next
  // to a diploma year. A pharmacist's experience always follows the year.
  if (isTechnicianWithStatedExperience(profile)) {
    return profile;
  }

  return {
    ...profile,
    yearsOfExperience: currentYear - experienceStartYear,
  };
};

export const getProfileWithCalculatedYearsOfExperience = (
  profile: SaveOnboardingDetailsFields,
  currentYear = new Date().getFullYear(),
): SaveOnboardingDetailsFields => {
  const experienceStartYear = getExperienceStartYear(profile);

  if (isNonNegativeNumber(experienceStartYear)) {
    return getProfileWithExperienceFromStartYear(
      profile,
      experienceStartYear,
      currentYear,
    );
  }

  if (!isTechnicianWithStatedExperience(profile)) {
    return profile;
  }

  // #technician-changes: the backend requires a diploma year, so a technician
  // who only stated years of experience gets the year that experience began.
  return {
    ...profile,
    techGraduationYear: currentYear - profile.yearsOfExperience,
  };
};

export const getAiOnboardingContactPhoneNumber = (
  profile: Pick<SaveOnboardingDetailsFields, 'phone' | 'mobile'>,
) =>
  (isNonEmptyString(profile.phone) ? profile.phone as string : profile.mobile);

const hasRoleSpecificProfileFields = (
  profile: Partial<IAiOnboardingProfileFields>,
) => {
  if (profile.role === TypeOfUser.student) {
    return true;
  }

  if (
    !isNonNegativeNumber(profile.graduationYear)
    || !isNonNegativeNumber(profile.yearsOfExperience)
  ) {
    return false;
  }

  if (profile.role === TypeOfUser.pharmacist) {
    return isNonEmptyString(profile.licenseNumber);
  }

  // #technician-changes: the diploma is optional because a technician may give
  // years of experience instead, which is already required above.
  return profile.role === TypeOfUser.technician
    && requiredTechnicianProfileFieldNames.every((fieldName) =>
      isPresent(profile[fieldName]));
};

// #technician-changes: only technicians are not asked for a DSQ key.
const hasDsqKeyAnswer = (profile: Partial<IAiOnboardingProfileFields>) =>
  profile.role === TypeOfUser.technician
  || isPresent(profile.isDSQKeyAvailable);

const hasRequiredProfileFields = (
  profile: Partial<IAiOnboardingProfileFields>,
) =>
  requiredAiOnboardingProfileFieldNames.every((fieldName) =>
    profile[fieldName] !== undefined && profile[fieldName] !== null)
  && requiredNonEmptyStringFieldNames.every((fieldName) =>
    isNonEmptyString(profile[fieldName]))
  && Array.isArray(profile.selectedContactPreferences)
  && profile.selectedContactPreferences.length > 0
  && Array.isArray(profile.selectedSoftwares)
  && Array.isArray(profile.preferredWorkingDays)
  && hasDsqKeyAnswer(profile)
  && hasRoleSpecificProfileFields(profile);

export const mapOnboardingDetails = (
  fields: SaveOnboardingDetailsFields,
): IAiOnboardingProfileFields => {
  const {
    licenseNumber,
    graduationYear,
    yearsOfExperience,
    phone,
    preferredDistanceRange,
    latitude,
    longitude,
    diploma,
    instituteName,
    techGraduationYear,
    certificateFileName,
    isDSQKeyAvailable,
    isNonSterilCompoundPrepKnowledge,
    handlingHospitalDischargeRating,
    isClickSanteKnowledge,
    isEmpegoKnowledge,
    selectedSoftwares,
    selectedContactPreferences,
    preferredWorkingDays,
    ...details
  } = getProfileWithCalculatedYearsOfExperience(fields);

  return {
    ...details,
    licenseNumber: licenseNumber?.trim() || null,
    graduationYear: graduationYear ?? null,
    yearsOfExperience: yearsOfExperience ?? null,
    phone: normalizeAiOnboardingPhoneNumber(
      getAiOnboardingContactPhoneNumber({ mobile: details.mobile, phone }),
    ),
    mobile: normalizeAiOnboardingPhoneNumber(details.mobile),
    postalCode: normalizeAiOnboardingPostalCode(details.postalCode),
    availabilityReminder: aiOnboardingAvailabilityReminder,
    preferredDistanceRange: preferredDistanceRange ?? null,
    latitude: latitude ?? null,
    longitude: longitude ?? null,
    diploma: diploma?.trim() || null,
    instituteName: instituteName?.trim() || null,
    techGraduationYear: techGraduationYear ?? null,
    certificateFileName: certificateFileName?.trim() || null,
    // #technician-changes: technicians are never asked for a DSQ key, which
    // manual signup also saves as false for them.
    isDSQKeyAvailable: isDSQKeyAvailable ?? false,
    isNonSterilCompoundPrepKnowledge: isNonSterilCompoundPrepKnowledge ?? null,
    handlingHospitalDischargeRating: handlingHospitalDischargeRating ?? null,
    isClickSanteKnowledge: isClickSanteKnowledge ?? null,
    isEmpegoKnowledge: isEmpegoKnowledge ?? null,
    selectedSoftwares: selectedSoftwares.map((software) =>
      ({ ...software })),
    selectedContactPreferences: [...selectedContactPreferences],
    preferredWorkingDays: [...preferredWorkingDays],
  };
};

export const getAiOnboardingFinalProfileFields = (
  snapshot: IAiOnboardingSnapshot,
  signupRequestId: string,
): IAiOnboardingFinalProfileFields => {
  const hydratedProfile = snapshot.onboardingDetails;

  if (!hydratedProfile) {
    throw new Error('AI onboarding profile fields are incomplete');
  }

  const profile = getProfileWithCalculatedYearsOfExperience(hydratedProfile);

  if (!hasRequiredProfileFields(profile)) {
    throw new Error('AI onboarding profile fields are incomplete');
  }

  return {
    ...profile,
    licenseNumber: profile.licenseNumber ?? null,
    graduationYear: profile.graduationYear ?? null,
    yearsOfExperience: profile.yearsOfExperience ?? null,
    phone: normalizeAiOnboardingPhoneNumber(
      getAiOnboardingContactPhoneNumber(profile),
    ),
    mobile: normalizeAiOnboardingPhoneNumber(profile.mobile),
    postalCode: normalizeAiOnboardingPostalCode(profile.postalCode),
    availabilityReminder: aiOnboardingAvailabilityReminder,
    preferredDistanceRange: profile.preferredDistanceRange ?? null,
    latitude: profile.latitude ?? null,
    longitude: profile.longitude ?? null,
    diploma: profile.diploma ?? null,
    instituteName: profile.instituteName ?? null,
    techGraduationYear: profile.techGraduationYear ?? null,
    certificateFileName: profile.certificateFileName ?? null,
    // #technician-changes: see mapOnboardingDetails.
    isDSQKeyAvailable: profile.isDSQKeyAvailable ?? false,
    isNonSterilCompoundPrepKnowledge:
      profile.isNonSterilCompoundPrepKnowledge ?? null,
    handlingHospitalDischargeRating:
      profile.handlingHospitalDischargeRating ?? null,
    isClickSanteKnowledge: profile.isClickSanteKnowledge ?? null,
    isEmpegoKnowledge: profile.isEmpegoKnowledge ?? null,
    signupRequestId,
  } as IAiOnboardingFinalProfileFields;
};
